import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, Spinner, ArrowSquareOut, CheckCircle, Warning, X } from "@phosphor-icons/react";
import { fetchMyRecentBriefings } from "@/lib/api";
import { useT } from "@/lib/i18n";

/**
 * Compact "Briefing Activity" widget — shows the rep their last few pre-call
 * warranty briefing SMS deliveries. Only renders when there's at least one row
 * (avoids cluttering the landing page for users who never receive briefings).
 *
 * Surfaces the same fields the audit log shows on /admin/call-routing → Briefings,
 * but trimmed for the landing-page sidebar context.
 */
export default function MyBriefingsCard() {
  const [data, setData] = useState({ items: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const t = useT();

  useEffect(() => {
    let cancelled = false;
    fetchMyRecentBriefings(10)
      .then((d) => { if (!cancelled) setData(d || { items: [], summary: {} }); })
      .catch(() => { if (!cancelled) setData({ items: [], summary: {} }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-md border border-slate-200 p-3 bg-white" data-testid="my-briefings-loading">
        <Spinner size={14} className="animate-spin text-slate-400" />
      </div>
    );
  }
  if (!data.items?.length) return null;

  const sent = data.summary?.sent || 0;
  const failed = data.summary?.failed || 0;
  const skipped = data.summary?.skipped || 0;

  return (
    <div className="rounded-md border border-slate-200 bg-white" data-testid="my-briefings-card">
      <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
        <Wrench size={14} weight="fill" className="text-blue-700" />
        <h3 className="text-xs font-bold tracking-tight font-['Chivo']">{t("briefings.title", "My Pre-call Briefings")}</h3>
        <span className="text-[10px] text-slate-500 ml-auto">
          {t("briefings.summary_sent", "{n} sent").replace("{n}", sent)}
          {failed > 0 && (<>{" · "}<strong className="text-rose-700">{t("briefings.summary_failed", "{n} failed").replace("{n}", failed)}</strong></>)}
          {" · "}{t("briefings.summary_skipped", "{n} skipped").replace("{n}", skipped)}
        </span>
      </div>
      <ul className="divide-y divide-slate-100">
        {data.items.slice(0, 5).map((it) => (
          <li key={it.id} className="px-3 py-2 flex items-start gap-2 text-xs hover:bg-slate-50" data-testid={`my-briefing-${it.id}`}>
            <OutcomeBadge outcome={it.outcome} />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-slate-800 truncate" title={it.company_name || it.caller_phone}>
                {it.company_name || it.caller_phone || t("briefings.unknown_caller", "Unknown caller")}
              </div>
              <div className="text-[10px] text-slate-500 truncate">
                {it.claim_ticket && (
                  <a href={it.claim_id ? `/warranty/claims/${it.claim_id}` : "#"}
                     className="text-blue-700 hover:underline font-mono">
                    {it.claim_ticket}
                  </a>
                )}
                {it.claim_status && <span className="ml-1 capitalize">· {it.claim_status.replace(/_/g, " ")}</span>}
                {it.error && <span className="ml-1 text-rose-700">· {it.error.slice(0, 60)}</span>}
              </div>
            </div>
            <span className="text-[9px] font-mono text-slate-400 whitespace-nowrap">
              {formatRelative(it.created_at, t)}
            </span>
          </li>
        ))}
      </ul>
      <button onClick={() => navigate("/admin/call-routing")}
              className="w-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800 border-t border-slate-200 hover:bg-slate-50 inline-flex items-center justify-center gap-1"
              data-testid="my-briefings-view-all">
        {t("briefings.open_audit", "Open full audit log")} <ArrowSquareOut size={10} weight="bold" />
      </button>
    </div>
  );
}

function OutcomeBadge({ outcome }) {
  const o = (outcome || "").toLowerCase();
  if (o === "sent") return <CheckCircle size={14} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />;
  if (o === "failed") return <Warning size={14} weight="fill" className="text-rose-600 shrink-0 mt-0.5" />;
  return <X size={14} weight="bold" className="text-slate-400 shrink-0 mt-0.5" />;
}

function formatRelative(iso, t) {
  if (!iso) return "";
  try {
    const ts = new Date(iso).getTime();
    const diff = Date.now() - ts;
    const min = Math.floor(diff / 60000);
    if (min < 1) return t ? t("briefings.now", "now") : "now";
    if (min < 60) return (t ? t("briefings.minutes_ago", "{n}m ago") : "{n}m ago").replace("{n}", min);
    const hr = Math.floor(min / 60);
    if (hr < 24) return (t ? t("briefings.hours_ago", "{n}h ago") : "{n}h ago").replace("{n}", hr);
    const d = Math.floor(hr / 24);
    return (t ? t("briefings.days_ago", "{n}d ago") : "{n}d ago").replace("{n}", d);
  } catch {
    return "";
  }
}
